/**
 * Maintains a reference counted map of unique strings. As elements are added and removed a reference
 * count is maintained. When the count falls to zero the item is removed from the collection.
 */
export class ReferenceCountedMap<T, IndexT = string> {
    private _map = new Map<
        IndexT,
        {
            value: T;
            refCount: number;
        }
    >();

    constructor(private readonly _prop?: string) {}

    [Symbol.iterator]() {
        return this._map.values();
    }

    /**
     * Add a new value to the map.
     * @param value string to add
     * @returns True if a new entry was created, false if it already existed
     */
    public add(value: T): boolean {
        const key = this._getKey(value);
        const cur = this._map.get(key);
        if (cur) {
            ++cur.refCount;
            return false;
        }
        this._map.set(key, {
            value,
            refCount: 0,
        });
        return true;
    }

    /**
     * Remove a new value from the map.
     * @param key of the item to remove
     * @returns True if the entry deleted, false if it still exists
     */
    public delete(key: IndexT): boolean {
        const cur = this._map.get(key);
        if (cur) {
            --cur.refCount;
            if (cur.refCount === 0) {
                this._map.delete(key);
                return true;
            }
        }
        return false;
    }

    public get(key: IndexT): T | undefined {
        return this._map.get(key)?.value;
    }

    public has(key: IndexT): boolean {
        return this._map.has(key);
    }

    private _getKey(value: T): IndexT {
        if (this._prop) {
            return (value as any)[this._prop];
        }
        return value as unknown as IndexT;
    }
}
